import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { scenarioVocabularyApi } from '../../api/ontologyApi';
import { Code, Section } from './DetailPrimitives';
import { scenarioVocabularyHref } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

interface DetectingScenario {
    code: string;
    ruleName: string | null;
    approvedBy?: string | null;
    approvedAt?: string | null;
}

/**
 * On a typology's page: which of the institution's own monitoring rules are approved as
 * detecting it. The other half of Ontology → Your vocabulary, so an analyst reading
 * "Structuring" can see that STRUCT_01 is what raises it here.
 *
 * Says so when no rule is mapped: that is the gap worth seeing, not an empty heading.
 */
const DetectedByScenariosSection: React.FC<{ typologyCode: string }> = ({ typologyCode }) => {
    const [scenarios, setScenarios] = useState<DetectingScenario[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setScenarios(null);
        setFailed(false);
        scenarioVocabularyApi.forTypology(typologyCode, { signal: controller.signal })
            .then((res: any) => setScenarios((res?.data ?? res)?.scenarios ?? []))
            .catch((err: any) => {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setFailed(true);
            });
        return () => controller.abort();
    }, [typologyCode]);

    // A failed lookup hides the section: the page is about the typology, and this is one
    // institution-specific aside on it.
    if (failed || scenarios === null) return null;

    return (
        <Section title="Detected in your monitoring system by" always>
            {scenarios.length === 0 ? (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted') }}>
                    None of your detection scenarios is mapped to this typology.{' '}
                    <Box component="a" href={scenarioVocabularyHref(null)} sx={{ color: dsVar('accent-fg'), textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        Map scenarios in Your vocabulary
                    </Box>
                </Typography>
            ) : (
                <Box className="flex flex-col gap-1.5">
                    {scenarios.map((s) => (
                        <Box
                            key={s.code}
                            component="a"
                            href={scenarioVocabularyHref(s.code)}
                            sx={{ display: 'flex', gap: 1, alignItems: 'baseline', textDecoration: 'none', '&:hover span:first-of-type': { textDecoration: 'underline' } }}
                        >
                            <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600, color: dsVar('accent-fg') }}>
                                {s.ruleName || s.code}
                            </Typography>
                            {s.ruleName && <Code>{s.code}</Code>}
                        </Box>
                    ))}
                </Box>
            )}
        </Section>
    );
};

export default DetectedByScenariosSection;
